/**
 * Pieces of the V1 godown boards that the gate kit does not have: the title row with a second
 * line, the green "done" screen, the red refusal card, numbered lines, the truck row, totals.
 * Values (sizes, colours, radii) are copied from the boards' inline styles.
 */
import { useState, type ReactNode } from 'react'
import { useNetworkStatus } from '@/api'
import { downloadCaches, storeCacheAge, useSyncStatus } from '@/offline'
import { env } from '@/lib/env'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  StepDots,
  Strip,
  V1Header,
  V1Screen,
  WaitCta,
} from '@/features/gate/v1/kit'
import { Rich } from '@/features/gate/v1/Rich'
import { C } from '@/features/gate/v1/style'
import { tr } from '@/features/gate/v1/text'
import type { StoreLot } from '@/offline/storeCache'
import type { StoreState } from '../projection'
import { useNow, useStoreState } from '../useStore'
import { SIcon, type StoreIconName } from './icons'
import { lotHoldLine, sinceParts } from './logic'

/** "40 मिनट" / "2 घंटे" / "3 दिन" since `iso`. */
export function agoText(iso: string, now: number): string {
  const p = sinceParts(now - new Date(iso).getTime())
  return tr(`store.v1.ago.${p.unit}`, { n: p.n })
}

/** The header: back, records to send, मदद; then the step icon, the title (and a quiet line), the dots. */
export function StoreHeader({
  onBack,
  icon,
  title,
  sub,
  step,
}: {
  onBack?: () => void
  icon: StoreIconName
  title: string
  /** The keeper's name on the home board. */
  sub?: string
  /** 1-based, of 2 (the godown flows are two steps). */
  step?: number
}) {
  const sync = useSyncStatus()
  return (
    <>
      <V1Header pending={sync.pending} onBack={onBack} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 6px' }}>
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 18,
            background: C.acc,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SIcon name={icon} size={22} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 21, lineHeight: 1.2, fontWeight: 700 }}>{title}</h1>
          {sub && <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.15 }}>{sub}</span>}
        </span>
        {step !== undefined && <StepDots step={step} of={2} />}
      </div>
    </>
  )
}

/** Coloured square behind an icon (truck, godown, eye...). */
export function IconBox({
  icon,
  size = 52,
  iconSize = 30,
  filled = false,
  color = C.acc,
  background = C.sunk,
}: {
  icon: StoreIconName
  size?: number
  iconSize?: number
  /** Brown square, white icon (the first arriving vehicle). */
  filled?: boolean
  color?: string
  background?: string
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 10,
        background: filled ? C.acc : background,
        color: filled ? '#ffffff' : color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SIcon name={icon} size={iconSize} />
    </span>
  )
}

/** Beige line with the icon in a white square ("नंबर प्लेट देखकर चुनें"). */
export function Hint({
  icon,
  text,
  tone = 'sunk',
  size = 17,
}: {
  icon: StoreIconName
  text: string
  /** Orange wash (match later). */
  tone?: 'sunk' | 'warn'
  size?: number
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 12,
        background: tone === 'warn' ? C.warnWash : C.sunk,
      }}
    >
      <IconBox
        icon={icon}
        size={40}
        iconSize={24}
        background={C.surface}
        color={tone === 'warn' ? C.accDark : C.acc}
      />
      <span style={{ fontSize: size, lineHeight: 1.35 }}>
        <Rich text={text} />
      </span>
    </div>
  )
}

/** The green line under the header after a good scan. */
export function Saved({ text }: { text: string }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 12,
        background: C.ok,
        color: '#ffffff',
        fontSize: 17,
        fontWeight: 700,
      }}
    >
      <SIcon name="ok" size={22} sw={3} />
      {text}
    </div>
  )
}

/** "5/8 चढ़ीं" on the right of a row. */
export function Tally({ value, label }: { value: string; label: string }) {
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        flexShrink: 0,
        lineHeight: 1.1,
      }}
    >
      <b style={{ fontSize: 24, whiteSpace: 'nowrap', color: C.ink }}>{value}</b>
      <span style={{ fontSize: 14, color: C.muted }}>{label}</span>
    </span>
  )
}

/** A vehicle: the truck square, the number plate, a line under it. */
export function VehicleRow({
  vehicle,
  line,
  quiet,
  extra,
  right,
  onClick,
  picked,
  first,
  chevron,
  box = 52,
}: {
  vehicle: string
  line?: ReactNode
  /** Grey line (पिछली बार …). */
  quiet?: boolean
  /** Third line (clock · 2 घंटे). */
  extra?: ReactNode
  right?: ReactNode
  onClick?: () => void
  /** Picked: 3px ink border and the black tick. */
  picked?: boolean
  /** The first arriving vehicle: brown edge and a brown square. */
  first?: boolean
  /** The brown › (the row leads on). */
  chevron?: boolean
  box?: number
}) {
  return (
    <ItemRow
      onClick={onClick}
      padding={first ? '7px 10px' : box === 52 ? '8px 10px' : '10px 12px'}
      border={picked ? C.ink : first ? C.acc : onClick ? C.border : C.rule}
      borderWidth={picked ? 3 : first ? 2 : 1.5}
      titleSize={box === 52 && !first ? 19 : 18}
      lead={<IconBox icon="truck" size={box} iconSize={box === 52 ? 30 : 26} filled={first} />}
      title={vehicle}
      line={
        line || extra ? (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {line && (
              <span style={{ fontSize: box === 52 ? 15 : 16, color: quiet ? C.muted : C.ink }}>
                {line}
              </span>
            )}
            {extra && <span style={{ fontSize: 15, color: C.muted }}>{extra}</span>}
          </span>
        ) : undefined
      }
      right={
        right ??
        (picked ? (
          <span
            aria-hidden="true"
            style={{
              width: 26,
              height: 26,
              flexShrink: 0,
              borderRadius: 13,
              background: C.ink,
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SIcon name="ok" size={15} sw={3} />
          </span>
        ) : chevron ? (
          <span style={{ color: C.acc, display: 'flex' }}>
            <SIcon name="next" size={26} sw={2.5} />
          </span>
        ) : undefined)
      }
    />
  )
}

/** A step line with its number in a ring ("1  छपे फ़ॉर्म पर साइन करें"). */
export function NumberedLine({
  n,
  text,
  size = 17,
  ring = 28,
}: {
  n: number
  text: string
  size?: number
  ring?: number
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: ring > 24 ? 10 : 8,
        alignItems: ring > 24 ? 'center' : 'flex-start',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: ring,
          height: ring,
          flexShrink: 0,
          borderRadius: ring / 2,
          border: `2px solid ${C.ink}`,
          color: C.ink,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: ring > 24 ? 16 : 14,
          fontWeight: 700,
          boxSizing: 'border-box',
          lineHeight: 1,
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: size, lineHeight: ring > 24 ? 1.3 : 1.35 }}>
        <Rich text={text} />
      </span>
    </div>
  )
}

/** The red refusal card: what was refused, why, and the two things to do. */
export function RefusedCard({
  title,
  why,
  steps,
}: {
  title: string
  why: string
  steps: string[]
}) {
  return (
    <section
      role="alert"
      style={{
        background: C.badWash,
        border: `2px solid ${C.bad}`,
        borderRadius: 12,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 4,
            background: C.bad,
            color: '#ffffff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SIcon name="x" size={20} sw={3} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25, color: C.bad }}>
            {title}
          </span>
          <span style={{ fontSize: 16, color: C.ink, lineHeight: 1.3 }}>{why}</span>
        </span>
      </div>
      <b style={{ fontSize: 16 }}>{tr('store.v1.refused.what')}</b>
      {steps.map((s, i) => (
        <NumberedLine key={i} n={i + 1} text={s} size={16} ring={24} />
      ))}
    </section>
  )
}

/** The supervisor's number, when the godown list carries one (config.supervisor_phone). */
export function supervisorPhone(state: StoreState | null): string | null {
  const v = state?.cache.config.supervisor_phone
  return typeof v === 'string' && /^\+?\d{10,13}$/.test(v.replace(/\s/g, '')) ? v : null
}

/** Full-screen refusal (scan refused at loading, problem bag at the godown). */
export function RefusedScreen({
  icon,
  title,
  step,
  card,
  phone,
  onBack,
  onOk,
}: {
  icon: StoreIconName
  title: string
  step: number
  card: ReactNode
  phone: string | null
  onBack: () => void
  onOk: () => void
}) {
  return (
    <V1Screen
      header={<StoreHeader onBack={onBack} icon={icon} title={title} step={step} />}
      footer={
        <Footer>
          {phone && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn icon="call" onClick={() => window.open(`tel:${phone.replace(/\s/g, '')}`)}>
                {tr('store.v1.supervisor')}
              </Btn>
            </div>
          )}
          <Cta label={tr('store.v1.refused.gotIt')} icon="ok" onClick={onOk} />
        </Footer>
      }
    >
      {card}
    </V1Screen>
  )
}

/** White card of big figures (लॉट 11 · बोरी 78 · माल किलो 3,742.65). */
export function Totals({ rows }: { rows: Array<[string, string]> }) {
  return (
    <section
      style={{
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        borderRadius: 12,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {rows.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            fontWeight: 700,
          }}
        >
          <span style={{ fontSize: 17 }}>{k}</span>
          <span style={{ fontSize: 26, fontWeight: 700, whiteSpace: 'nowrap' }}>{v}</span>
        </div>
      ))}
    </section>
  )
}

/** The green done screen (गाड़ी रवाना · माल लिया · फ़ॉर्म जमा) with घर at the foot. */
export function DoneScreen({
  title,
  lines,
  chips,
  onHome,
}: {
  title: string
  lines: ReactNode
  chips: Array<{ icon: StoreIconName; text: string }>
  onHome: () => void
}) {
  return (
    <V1Screen
      main={{ padding: '0', gap: 0 }}
      footer={
        <Footer>
          <Cta label={tr('store.v1.home')} icon="home" lead onClick={onHome} />
        </Footer>
      }
    >
      <div
        role="status"
        style={{
          flexGrow: 1,
          background: C.ok,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 20,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            background: '#ffffff',
            color: C.ok,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SIcon name="ok" size={76} sw={3.2} />
        </span>
        <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.2 }}>{title}</h1>
        <span style={{ fontSize: 20, lineHeight: 1.4 }}>{lines}</span>
        {chips.map((c) => (
          <span
            key={c.text}
            style={{
              fontSize: 18,
              lineHeight: 1.4,
              padding: '8px 12px',
              borderRadius: 12,
              background: 'rgba(0,0,0,.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <SIcon name={c.icon} size={22} />
            <span>
              <Rich text={c.text} />
            </span>
          </span>
        ))}
      </div>
    </V1Screen>
  )
}

/** "सूची ताज़ा करें": GET the godown list again (needs the internet). */
export function RefreshButton({ label = tr('store.v1.home.refresh') }: { label?: string }) {
  const { online } = useNetworkStatus()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  return (
    <>
      <Btn
        icon="download"
        disabled={!online || busy}
        onClick={() => {
          setBusy(true)
          setFailed(false)
          downloadCaches()
            .catch(() => setFailed(true))
            .finally(() => setBusy(false))
        }}
      >
        {online ? label : tr('store.v1.cache.offline')}
      </Btn>
      {failed && (
        <span role="alert" style={{ fontSize: 16, fontWeight: 700, color: C.bad }}>
          {tr('store.v1.cache.failed')}
        </span>
      )}
    </>
  )
}

/** The godown list (store cache), or the board-style screen asking to download it. */
export function WithStore({
  onBack,
  children,
}: {
  onBack?: () => void
  children: (state: StoreState) => ReactNode
}) {
  const { loading, state } = useStoreState()
  const { online } = useNetworkStatus()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  if (loading) return <V1Screen />
  if (state) return <>{children(state)}</>
  return (
    <V1Screen
      header={<StoreHeader onBack={onBack} icon="download" title={tr('store.v1.cache.title')} />}
      footer={
        <Footer>
          {online ? (
            <Cta
              label={tr('store.v1.cache.download')}
              icon="download"
              busy={busy}
              onClick={() => {
                setBusy(true)
                setFailed(false)
                downloadCaches()
                  .catch(() => setFailed(true))
                  .finally(() => setBusy(false))
              }}
            />
          ) : (
            <WaitCta>{tr('store.v1.cache.offline')}</WaitCta>
          )}
        </Footer>
      }
    >
      <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4 }}>{tr('store.v1.cache.body')}</p>
      {failed && (
        <span role="alert" style={{ fontSize: 16, fontWeight: 700, color: C.bad }}>
          {tr('store.v1.cache.failed')}
        </span>
      )}
    </V1Screen>
  )
}

/** "सूची पुरानी है" when the godown list is a day old or more. */
export function StaleStrip({ state }: { state: StoreState }) {
  const now = useNow()
  const age = storeCacheAge(state.downloadedAt, env.rosterMaxAgeDays, now)
  if (age === 'fresh') return null
  return <Strip icon="bang">{tr('store.v1.cache.stale')}</Strip>
}

/**
 * The red line under a lot the office has stopped, with the reason in plain words; undefined when
 * the lot is not on hold (so the row keeps no empty line). Shown only — it blocks nothing.
 */
export function holdNote(lot: Pick<StoreLot, 'hold' | 'hold_reason'> | null | undefined) {
  const text = lotHoldLine(lot)
  if (!text) return undefined
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: C.bad,
        fontWeight: 700,
        fontSize: 15,
      }}
    >
      <SIcon name="lock" size={16} />
      {text}
    </span>
  )
}
